// src/components/auth/Login.jsx
import React, { useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FaEnvelope, FaLock, FaGoogle } from 'react-icons/fa';

// OPTIMIZATION: Extracted form input into a reusable component
const FormInput = React.memo(({ type, id, value, onChange, placeholder, disabled, icon, label, ...props }) => (
  <div className="form-group">
    {label && <label htmlFor={id}>{label}</label>}
    <div className="input-with-icon">
      {icon}
      <input
        type={type}
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required
        {...props}
      />
    </div>
  </div>
));

const Login = () => {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // OPTIMIZATION: Use initialState object for form state
  const [formState, setFormState] = useState({
    email: '',
    password: '',
    loading: false,
    error: null
  });
  
  // OPTIMIZATION: Extract from state for better readability
  const { email, password, loading, error } = formState;
  
  // Get the redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || '';
  
  // OPTIMIZATION: Use useCallback for event handlers
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);
  
  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setFormState(prev => ({
        ...prev,
        error: 'Please enter both email and password'
      }));
      return;
    }
    
    setFormState(prev => ({
      ...prev,
      loading: true,
      error: null
    }));
    
    try {
      // Log in with email and password
      const result = await login(email, password);
      
      // Navigate to spotlight page
      navigate('/spotlight');
    } catch (error) {
      console.error('Login error:', error);
      let errorMsg = error.message || 'Failed to log in. Please check your credentials.';
      if (error.code === 'auth/invalid-login-credentials' || errorMsg.includes('auth/invalid-login-credentials')) {
        errorMsg = 'Incorrect email or password. Please try again.';
      }
      setFormState(prev => ({
        ...prev,
        error: errorMsg
      }));
    } finally {
      setFormState(prev => ({
        ...prev,
        loading: false
      }));
    }
  }, [email, password, login, navigate]);

  // Google login function
  const handleGoogleSignIn = useCallback(async () => {
  setFormState(prev => ({
    ...prev,
    loading: true,
    error: null
  }));
  
  try {
    // Sign in with Google (will try popup first, then redirect if needed)
    const result = await loginWithGoogle();
    
    // If we're redirecting, show a loading message
    if (result.redirecting) {
      console.log('Redirecting to Google for authentication...');
      // No navigation here - redirect will happen automatically
      return;
    }
    
    // This code only executes if popup was successful (no redirect)
    if (result.isNewUser) {
      // If new user, navigate to user type selection
      navigate('/auth/select-user-type', { 
        state: { from, uid: result.user.uid } 
      });
    } else {
      // Navigate to spotlight page for all existing users
      navigate('/spotlight');
    }
  } catch (error) {
    console.error('Google sign-in error:', error);
    setFormState(prev => ({
      ...prev,
      error: error.message || 'Failed to sign in with Google'
    }));
    setFormState(prev => ({
      ...prev,
      loading: false
    }));
  }
}, [loginWithGoogle, navigate, from]);
  
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <div className="logo-icon">T</div>
            <h1>TotsPal</h1>
          </div>
          <h2>Welcome Back</h2>
          <p className="text-muted">Sign in to your TotsPal account</p>
        </div>
        
        {error && <div className="error-message prominent-error" role="alert">{error}</div>}
        
        <form onSubmit={handleSubmit} className="auth-form">
          <FormInput
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={handleInputChange}
            placeholder="Enter your email"
            disabled={loading}
            icon={<FaEnvelope className="input-icon" />}
            label="Email"
            aria-label="Email address"
          />
          
          <div className="form-group">
            <div className="password-header">
              <label htmlFor="password">Password</label>
              <Link to="/auth/reset-password" className="forgot-password">
                Forgot Password?
              </Link>
            </div>
            <div className="input-with-icon">
              <FaLock className="input-icon" />
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                disabled={loading}
                required
                aria-label="Password"
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        </form>
        
        <div className="auth-divider">
          <span>OR</span>
        </div>
        
        <button 
          type="button" 
          className="btn btn-outline btn-block social-login-btn"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <FaGoogle className="btn-icon" />
          <span>Sign in with Google</span>
        </button>
        
        <div className="auth-links">
          <p>
            Don't have an account? <Link to="/auth/register">Sign Up</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Login);